import { Button, Icon, InputField } from '@design-system';
import { useTranslation } from 'next-i18next';
import { FormEvent, PropsWithChildren, useState } from 'react';
import { AssetStoryblok } from '../../types/generated-storyblok';
import { Image } from '../../components/Image';

export const LockScreen = ({
  children,
  thumbnail,
  title,
  unlockCode,
}: PropsWithChildren<{
  title: string;
  thumbnail: AssetStoryblok;
  unlockCode: string;
}>) => {
  const { t } = useTranslation('releases');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [isUnlocked, setIsUnlocked] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (code === unlockCode) {
      setIsUnlocked(true);
    } else {
      setError(t('lockScreen.invalidCode'));
      setCode('');
    }
  };

  if (isUnlocked) {
    return <>{children}</>;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center gap-6 px-5"
    >
      <div className="relative h-24 w-24 overflow-hidden rounded-full md:h-32 md:w-32">
        <Image
          alt={thumbnail.alt || title}
          src={thumbnail.filename}
          width={128}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-6/50">
          <Icon className="text-white" name="ic_lock" size="md" />
        </div>
      </div>

      <h1 className="text-balance text-center text-md font-bold md:text-lg">
        {title}
      </h1>

      <div className="flex w-full flex-col gap-3">
        <InputField
          error={error}
          id="unlock-code"
          inputmode="numeric"
          maxLength={6}
          name="unlock-code"
          pattern="[0-9]{6}"
          placeholder="000000"
          textAlign="center"
          type="text"
          value={code}
          onChange={(e) => {
            setError(undefined);
            setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
          }}
        />

        <Button
          fullWidth
          label={t('lockScreen.ctaLabel')}
          type="submit"
          variant="primary"
          disabled={code.length !== 6}
        />
      </div>

      <p>
        {t('lockScreen.noCode')}{' '}
        <a href="mailto:info@avantarte.com" className="hyperlink">
          {t('lockScreen.contactUs')}
        </a>
      </p>
    </form>
  );
};
